<template>
  <div class="my-1">
    <p class="lead d-flex justify-content-between">
      <span>{{ action.node_data.title }}</span>
      <span class="small">{{ action.data.timestamp }}</span>
    </p>
    <b-form-group
      label="Cámara"
      label-for="camera"
    >
      <v-select
        v-model="selectedCamera"
        :options="cameras"
        :clearable="false"
        input-id="camera"
        :disabled="terminated || validating"
        @input="onSelectCamera"
      />
    </b-form-group>

    <div class="player mb-2" v-if="selectedCamera && showPlayer">
      <camera-widget-v-2
        :key="`cam-${selectedCamera.value}`"
        :camera-proxy-id="selectedCamera.value"
        :date-range="dateRange"
        :config="{ layout: 'shared_video', download: false, synced: false }"
      />
    </div>

    <div class="player-demo" v-if="preview">
      <span>DEMO</span>
    </div>

    <div class="text-center" v-if="!preview">
      <span v-if="loadingImages">Cargando imágenes...</span>
      <snapshots-gallery v-if="images.length > 0" :images="images"/>
    </div>

    <span
      v-if="normalizedProcessMessage === 'max nvr sessions reached'"
      class="text-center text-danger d-block mb-1">
      Número máximo de sesiones del NVR alcanzado, espere unos minutos para descargar nuevamente.
    </span>
    <span v-else class="text-center text-danger d-block mb-1">
      {{ normalizedProcessMessage }}
    </span>
    <div class="download-buttons" v-if="!preview">
      <span v-if="loadingControls">Preparando controles...</span>
      <template v-if="selectedCamera && !loadingControls">
        <b-button
          variant="primary"
          size="sm"
          @click="fetchImages()"
          :disabled="isProcessing"
        >
          <b-spinner small v-if="isProcessing"></b-spinner>
          Vista previa de imágenes
        </b-button>

        <b-button
          variant="primary"
          size="sm"
          @click="downloadVideo()"
          :disabled="isProcessing"
        >
          <b-spinner small v-if="isProcessing"></b-spinner>
          Descargar video
        </b-button>

        <b-button
          variant="primary"
          size="sm"
          @click="downloadImages()"
          :disabled="isProcessing"
        >
          <b-spinner small v-if="isProcessing"></b-spinner>
          Descargar imágenes
        </b-button>
      </template>
    </div>

    <div class="text-right mb-1">
      <b-button
        variant="primary"
        size="sm"
        @click="onContinueClick(action)"
        v-if="!action.data.continue && !terminated"
      >
        {{ $t("action_plan.viewer.continue") }}
        <feather-icon icon="ChevronRightIcon" size="18"/>
      </b-button>
    </div>
  </div>
</template>

<script>
import commandStackHelperMixin from "@/mixins/commandStackHelperMixin";
import axios from "@axios";
import CameraWidgetV2 from "@/views/components/GridView/widgets/CameraWidgetV2.vue";
import SnapshotsGallery from "./SnapshotsGallery.vue";
import moment from "moment-timezone";
import {BFormGroup} from "bootstrap-vue";
import vSelect from "vue-select";

const SECONDS_OFFSET = 60;
const LOADING_BUTTONS_TIME = 4 * 1000; // Seconds
// The websocket callback is the only exit of the three flows, so a job that
// never reports back would leave the buttons spinning for good
const PROCESS_TIMEOUT_TIME = 300 * 1000;

export default {
  name: "ShowVideo",
  components: {
    BFormGroup,
    vSelect,
    CameraWidgetV2,
    SnapshotsGallery,
  },
  mixins: [commandStackHelperMixin],
  props: {
    actionPlanLogUuid: String,
    alarmId: String | Number,
    action: Object,
    terminated: Boolean,
    preview: Boolean,
    validating: Boolean,
  },
  data() {
    return {
      loadingControls: true,
      isProcessing: false,
      cameras: [],
      selectedCamera: null,
      alarmTs: '',
      loadingImages: false,
      totalImages: 0,
      images: [],
      dateRange: {},
      jobs: [],
      snapshotsChannel: null,
      processTimeoutTimer: null,
      isSetActionPlanChannel: false,
      showPlayer: false,
      playerConfig: {
        layout: 'shared_video',
        download: false,
        synced: false
      },
      processMessage: {}
    }
  },
  created() {
    this.selectedCamera = this.action.data.camera || null;
  },
  async mounted() {
    try {
      if (this.preview) return;
      if (!this.alarmId) return;
      const {data} = await this.fetchAlarmInfo();
      this.alarmTs = data.created;
      this.cameras = data.cameras
        .filter((cam) => cam !== null)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((cam) => {
          return {
            value: cam.camera_proxy_id,
            label: cam.name,
            cameraId: cam.camera_id
          }
        });

      const alarmTs = this.alarmTs;

      const diffAlarmTsNow = moment().diff(alarmTs, 'seconds');
      this.dateRange = {
        start: moment(alarmTs)
          .tz('America/Mexico_City')
          .subtract(SECONDS_OFFSET, 'seconds')
          .format('YYYY-MM-DD HH:mm:ss'),
        end: diffAlarmTsNow < SECONDS_OFFSET
          ? moment(alarmTs).format('YYYY-MM-DD HH:mm:ss')
          : moment(alarmTs)
            .tz('America/Mexico_City')
            .add(SECONDS_OFFSET, 'seconds')
            .format('YYYY-MM-DD HH:mm:ss'),
      };

      this.showPlayer = true;

      setTimeout(() => this.loadingControls = false, LOADING_BUTTONS_TIME);
    } catch (e) {
      console.log('[ShowVideo] error fetching alarm info: ', e.message);
    }
  },
  beforeDestroy() {
    this.leaveSnapshotsChannel();
    this.clearProcessTimeout();

    if (this.isSetActionPlanChannel) {
      window.Echo.leave(`action-plan-${this.actionPlanLogUuid}`);
    }
  },
  computed: {
    normalizedProcessMessage() {
      return this.processMessage.message?.message || this.processMessage.message?.msg || '';
    }
  },
  methods: {
    fetchAlarmInfo() {
      return axios.get(`/v1/services/alarms/alarm-by-id?alarm_id=${this.alarmId}`);
    },
    onSelectCamera(data) {
      this.action.data.camera = data;
      this.leaveSnapshotsChannel();
      this.clearProcessTimeout();
      this.totalImages = 0;
      this.images = [];
      this.isProcessing = false;
      this.loadingImages = false;
      this.loadingControls = true;

      setTimeout(() => this.loadingControls = false, LOADING_BUTTONS_TIME);
    },
    leaveSnapshotsChannel() {
      if (!this.snapshotsChannel || !window.Echo) return;
      window.Echo.leave(this.snapshotsChannel);
      this.snapshotsChannel = null;
    },
    startProcessTimeout() {
      this.clearProcessTimeout();
      this.processTimeoutTimer = setTimeout(this.onProcessTimeout, PROCESS_TIMEOUT_TIME);
    },
    clearProcessTimeout() {
      if (!this.processTimeoutTimer) return;
      clearTimeout(this.processTimeoutTimer);
      this.processTimeoutTimer = null;
    },
    // The download channels are left listening on purpose: a late job still
    // opens its file.
    onProcessTimeout() {
      this.processTimeoutTimer = null;
      if (!this.isProcessing) return;
      this.leaveSnapshotsChannel();
      this.isProcessing = false;
      this.loadingImages = false;
      if (this.images.length) return;
      this.processMessage = {
        message: {message: 'No se recibió respuesta del NVR, vuelva a intentarlo.'}
      };
    },
    // The pictures arrive in batches: merging by sequence keeps a second
    // preview from duplicating them and an out-of-order batch from shuffling
    // the gallery.
    processImages({message}) {
      if (message.status !== 'ok') {
        this.isProcessing = false;
        return;
      }

      const incoming = message.images || [];
      if (!incoming.length) return;

      const key = (image) => (image.sequence != null ? image.sequence : image.url);
      const merged = [...this.images];

      incoming.forEach((image) => {
        const index = merged.findIndex((stored) => key(stored) === key(image));
        if (index === -1) merged.push(image);
        else merged.splice(index, 1, image);
      });

      // Payloads without a usable sequence keep their arrival order (sort is stable)
      this.images = merged.sort((a, b) => {
        const sequenceA = Number(a.sequence);
        const sequenceB = Number(b.sequence);
        if (!Number.isFinite(sequenceA) || !Number.isFinite(sequenceB)) return 0;
        return sequenceA - sequenceB;
      });
    },
    async downloadVideo() {
      this.processMessage = {};
      if (this.isProcessing) return;
      this.isProcessing = true;
      this.startProcessTimeout();

      try {
        const {data} = await axios.get(this.buildUrl());
        const jobId = data.data?.id?.[0];
        if (!jobId) throw new Error('download-video answered no job id');

        window.Echo.private(`video-download-${jobId}`).listen(".update", (data) => {
          this.clearProcessTimeout();

          if (data.message.status === 'ok') {
            window.Echo.leave(`video-download-${jobId}`);
            window.open(data.message.url, "_blank");
          }

          this.processMessage = data;
          this.isProcessing = false;
        });
      } catch (error) {
        console.error(error);
        this.clearProcessTimeout();
        this.isProcessing = false;
      }
    },
    async downloadImages() {
      this.processMessage = {};
      if (this.isProcessing) return;
      this.isProcessing = true;
      this.startProcessTimeout();

      try {
        const {data} = await axios.get(this.buildUrl({snapshot: true}));
        const jobId = data.data?.id?.[0];
        if (!jobId) throw new Error('download-video answered no job id');

        window.Echo.private(`video-download-${jobId}`).listen(".update", (data) => {
          this.clearProcessTimeout();

          if (data.message.status === 'ok') {
            window.Echo.leave(`video-download-${jobId}`);
            window.open(data.message.url, "_blank");
          }

          this.processMessage = data;
          this.isProcessing = false;
        });
      } catch (error) {
        console.error(error);
        this.clearProcessTimeout();
        this.isProcessing = false;
      }
    },
    async fetchImages() {
      this.processMessage = {};
      if (this.isProcessing) return;
      this.isProcessing = true;
      this.loadingImages = true;
      this.leaveSnapshotsChannel();
      this.images = [];
      this.startProcessTimeout();

      try {
        const {data} = await axios.get(this.buildUrl({
          snapshot: true,
          previewImgs: true
        }));
        const jobId = data.data?.id?.[0];
        if (!jobId) throw new Error('download-video answered no job id');
        this.snapshotsChannel = `preview-snapshots-${jobId}`;

        window.Echo.private(this.snapshotsChannel).listen(".update", (data) => {
          this.clearProcessTimeout();

          if (data.message.status === 'ok') {
            this.processImages(data);
          } else {
            this.leaveSnapshotsChannel();
          }

          if (data.message.isLastBatch) {
            this.leaveSnapshotsChannel();
          }

          this.processMessage = data;
          this.isProcessing = false;
          this.loadingImages = false;
        });
      } catch (error) {
        console.error(error);
        this.clearProcessTimeout();
        this.isProcessing = false;
        this.loadingImages = false;
      }
    },
    buildUrl(config = {}) {
      const provider = 'hanwha_camera';
      const cameraId = this.selectedCamera.cameraId;
      const baseUrl = `v1/services/download-video/${provider}/${cameraId}`;
      const params = new URLSearchParams({
        cameraId: cameraId,
        start: this.dateRange.start,
        end: this.dateRange.end,
        fileName: `export-${this.actionPlanLogUuid}`,
        signed: true,
        withTimestamp: false,
        snapshot: config.snapshot === true,
        interval: 1,
        snapshotsPreview: config.previewImgs === true,
        actionPlanId: config.previewImgs === true ? this.actionPlanLogUuid : false,
      });

      const videoConfig = {
        width: 768,
        height: 768,
        overlap: 0,
        videoFormat: 'mp4',
      };

      Object.entries(videoConfig).forEach(([key, value]) => {
        params.append(`videoConfig[${key}]`, value);
      });

      return `${baseUrl}?${params.toString()}`;
    },
    onContinueClick(action) {
      this.$emit("continue", action);
    },
  },
};
</script>

<style lang="scss" scoped>
.player {
  margin: 0 auto;
  width: 450px;
  height: 350px;
}

.player-demo {
  width: 425px;
  height: 325px;
  margin-bottom: 15px;
  background-color: #0c0c0c;
  display: flex;
  justify-content: center;
  align-items: center;

  span {
    display: block;
    color: #fff;
    font-size: 18px;
  }
}

.download-buttons {
  display: flex;
  justify-content: space-evenly;
  width: 500px;
  margin: 0 auto 15px;
}
</style>
